import React, { useState, useRef } from "react";
import {
	previewMaterialImport,
	importMaterials,
	exportMaterials,
	renameImportedMaterials,
} from "../../services/apiService";
import { formatCurrency } from "../../utils/formatters";
import "./MaterialImport.css";

/**
 * Material Import Component (PDF-Only with Smart AI Extraction)
 * 
 * Features:
 * - Upload vendor PDF price lists
 * - AI-powered extraction using Claude Haiku 4.5
 * - Automatic cost/sqft calculation from dimensions + sheet price
 * - Clean review table of extracted materials
 * - Dry-run validation before final import
 * 
 * The AI handles:
 * - Fraction parsing (3/8" → 0.375)
 * - Unit conversion (feet to inches)
 * - Cost calculation (sheet_price / area_sqft)
 * - Clean name generation
 */
function MaterialImport({ onImportComplete }) {
	const [file, setFile] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	
	// Extracted materials from AI
	const [extractedMaterials, setExtractedMaterials] = useState(null);
	const [extractionNotes, setExtractionNotes] = useState([]);
	
	// Material selection for import
	const [selectedMaterials, setSelectedMaterials] = useState(new Set());
	
	// Import options
	const [mode, setMode] = useState("update");
	
	// Import results
	const [importResult, setImportResult] = useState(null);

	// AI rename
	const [renameInstruction, setRenameInstruction] = useState("");
	const [renameSuggestions, setRenameSuggestions] = useState([]);
	const [renameError, setRenameError] = useState(null);
	const [renameLoading, setRenameLoading] = useState(false);
	
	const fileInputRef = useRef(null);

	const handleFileChange = (e) => {
		const selectedFile = e.target.files[0];
		if (selectedFile) {
			const isPdf = selectedFile.type === 'application/pdf' || 
			             selectedFile.name.toLowerCase().endsWith('.pdf');
			
			if (!isPdf) {
				setError("Please select a PDF file only");
				return;
			}
			
			setFile(selectedFile);
			setExtractedMaterials(null);
			setExtractionNotes([]);
			setSelectedMaterials(new Set());
			setImportResult(null);
			setRenameInstruction("");
			setRenameSuggestions([]);
			setRenameError(null);
			setError(null);
		}
	};

	const handleExtract = async () => {
		if (!file) {
			setError("Please select a PDF file first");
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const result = await previewMaterialImport(file, true);
			
			if (result.mode === "smart_extraction") {
				setExtractedMaterials(result.materials || []);
				setExtractionNotes(result.extraction_notes || []);
				// Select all materials by default
				setSelectedMaterials(new Set(result.materials?.map((_, i) => i) || []));
				setRenameSuggestions([]);
				setRenameError(null);
			} else {
				// Fallback mode - shouldn't happen with AI enabled
				setError("Extraction returned unexpected format. Please try again.");
			}
		} catch (err) {
			setError(err.message || err.error || "Failed to extract materials from PDF");
		} finally {
			setIsLoading(false);
		}
	};

	const handleToggleMaterial = (index) => {
		setSelectedMaterials(prev => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	};

	const handleSelectAll = () => {
		if (selectedMaterials.size === extractedMaterials?.length) {
			setSelectedMaterials(new Set());
		} else {
			setSelectedMaterials(new Set(extractedMaterials?.map((_, i) => i) || []));
		}
	};

	const handleSuggestRenames = async () => {
		if (!renameInstruction.trim()) {
			setRenameError("Add a rename instruction before requesting suggestions.");
			return;
		}
		if (!extractedMaterials || selectedMaterials.size === 0) {
			setRenameError("Select at least one material to rename.");
			return;
		}

		setRenameLoading(true);
		setRenameError(null);

		try {
			const materialsToRename = extractedMaterials
				.map((material, index) => ({ material, index }))
				.filter(({ index }) => selectedMaterials.has(index))
				.map(({ material, index }) => ({
					index,
					name: material.name,
					vendor_sku: material.vendor_sku || "",
					sheet_width_in: material.sheet_width_in,
					sheet_length_in: material.sheet_length_in,
				}));

			const result = await renameImportedMaterials(
				renameInstruction,
				materialsToRename
			);

			const suggestions = (result.suggestions || [])
				.filter((item) => typeof item.index === "number" && item.new_name);

			setRenameSuggestions(suggestions);
		} catch (err) {
			setRenameError(err.message || err.error || "Rename suggestions failed.");
		} finally {
			setRenameLoading(false);
		}
	};

	const applyRenameSuggestions = () => {
		if (!renameSuggestions.length) return;
		const suggestionsByIndex = new Map(
			renameSuggestions.map((item) => [item.index, item.new_name])
		);

		setExtractedMaterials((prev) =>
			prev.map((material, index) => {
				if (!suggestionsByIndex.has(index)) return material;
				return {
					...material,
					name: suggestionsByIndex.get(index),
				};
			})
		);
		setRenameSuggestions([]);
	};

	const handleImport = async (dryRun = false) => {
		if (!extractedMaterials || selectedMaterials.size === 0) {
			setError("No materials selected for import");
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			// Get only selected materials
			const materialsToImport = extractedMaterials.filter((_, i) => selectedMaterials.has(i));
			
			const result = await importMaterials(null, {
				mode,
				dryRun,
				materials: materialsToImport,
			});
			
			setImportResult(result);
			
			if (result.success && !dryRun && onImportComplete) {
				onImportComplete();
			}
		} catch (err) {
			setError(err.message || err.error || "Import failed");
		} finally {
			setIsLoading(false);
		}
	};

	const handleExport = async () => {
		setIsLoading(true);
		setError(null);

		try {
			const blob = await exportMaterials();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "materials_export.csv";
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch (err) {
			setError(err.message || err.error || "Export failed");
		} finally {
			setIsLoading(false);
		}
	};

	const handleReset = () => {
		setFile(null);
		setExtractedMaterials(null);
		setExtractionNotes([]);
		setSelectedMaterials(new Set());
		setImportResult(null);
		setRenameInstruction("");
		setRenameSuggestions([]);
		setRenameError(null);
		setError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const formatDimensions = (material) => {
		const w = material.sheet_width_in;
		const l = material.sheet_length_in;
		if (w && l) {
			return `${w}"×${l}"`;
		}
		return "—";
	};

	return (
		<div className="material-import">
			<h3>📄 Import Materials from PDF Price Lists</h3>

			{/* Export Button */}
			<div className="import-actions-top">
				<button
					onClick={handleExport}
					disabled={isLoading}
					className="btn-secondary"
				>
					📥 Export Current Materials (CSV)
				</button>
			</div>

			<hr />

			{/* File Upload Section */}
			<div className="upload-section">
				<h4>Upload Vendor PDF Price List</h4>
				
				<div className="file-input-row">
					<input
						type="file"
						accept=".pdf,application/pdf"
						onChange={handleFileChange}
						ref={fileInputRef}
						disabled={isLoading}
					/>
					{file && (
						<span className="file-name">📄 {file.name}</span>
					)}
				</div>

				<p className="helper-text">
					🤖 Our AI will automatically extract materials, parse dimensions, 
					and calculate cost per square foot from your vendor price sheets.
				</p>

				<button
					onClick={handleExtract}
					disabled={!file || isLoading}
					className="btn-primary"
				>
					{isLoading ? "🔍 Extracting..." : "🚀 Extract Materials"}
				</button>
			</div>

			{/* Error Display */}
			{error && (
				<div className="error-message">
					<strong>Error:</strong> {error}
				</div>
			)}

			{/* Extracted Materials Preview */}
			{extractedMaterials && !importResult && (
				<div className="extraction-results">
					<div className="extraction-header">
						<h4>📊 Extracted Materials ({extractedMaterials.length})</h4>
						<button onClick={handleReset} className="btn-text">
							Reset
						</button>
					</div>

					{/* AI Notes */}
					{extractionNotes.length > 0 && (
						<div className="extraction-notes">
							<strong>🤖 AI Notes:</strong>
							<ul>
								{extractionNotes.map((note, i) => (
									<li key={i}>{note}</li>
								))}
							</ul>
						</div>
					)}

					<div className="ai-rename-panel">
						<div className="ai-rename-header">
							<h4>🤖 AI Rename (selected only)</h4>
							<span className="selected-count">
								{selectedMaterials.size} selected
							</span>
						</div>
						<p className="helper-text">
							Describe the rename rule (e.g., “Replace BG with ACRE for sheet items”).
						</p>
						<textarea
							className="ai-rename-input"
							rows={3}
							value={renameInstruction}
							onChange={(e) => setRenameInstruction(e.target.value)}
							placeholder="Example: Replace “BG” with “ACRE” for sheet items, keep dimensions and thickness."
							disabled={renameLoading}
						/>
						{renameError && (
							<div className="warning-message">{renameError}</div>
						)}
						<div className="ai-rename-actions">
							<button
								onClick={handleSuggestRenames}
								disabled={renameLoading || selectedMaterials.size === 0}
								className="btn-secondary"
							>
								{renameLoading ? "Thinking..." : "✨ Suggest Renames"}
							</button>
							{renameSuggestions.length > 0 && (
								<button
									onClick={applyRenameSuggestions}
									className="btn-primary"
								>
									✅ Apply Renames
								</button>
							)}
						</div>
						{renameSuggestions.length > 0 && (
							<div className="ai-rename-preview">
								<h5>Preview</h5>
								<ul>
									{renameSuggestions.slice(0, 10).map((item) => (
										<li key={item.index}>
											<strong>#{item.index + 1}:</strong>{" "}
											{extractedMaterials?.[item.index]?.name || "—"} →{" "}
											{item.new_name}
										</li>
									))}
									{renameSuggestions.length > 10 && (
										<li className="more">
											...and {renameSuggestions.length - 10} more
										</li>
									)}
								</ul>
							</div>
						)}
					</div>

					{/* Materials Table */}
					<div className="materials-preview-table">
						<table>
							<thead>
								<tr>
									<th>
										<input
											type="checkbox"
											checked={selectedMaterials.size === extractedMaterials.length}
											onChange={handleSelectAll}
										/>
									</th>
									<th>Material Name</th>
									<th>$/SqFt</th>
									<th>Sheet Size</th>
									<th>$/Sheet</th>
									<th>SKU</th>
								</tr>
							</thead>
							<tbody>
								{extractedMaterials.map((material, idx) => (
									<tr 
										key={idx} 
										className={selectedMaterials.has(idx) ? "selected" : ""}
									>
										<td>
											<input
												type="checkbox"
												checked={selectedMaterials.has(idx)}
												onChange={() => handleToggleMaterial(idx)}
											/>
										</td>
										<td className="material-name">{material.name}</td>
										<td className="cost-sqft">
											{formatCurrency(material.cost_per_sqft)}
										</td>
										<td className="dimensions">
											{formatDimensions(material)}
										</td>
										<td className="cost-sheet">
											{material.cost_per_sheet 
												? formatCurrency(material.cost_per_sheet) 
												: "—"
											}
										</td>
										<td className="sku">
											{material.vendor_sku || "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Import Settings */}
					<div className="import-settings">
						<label>
							Import Mode:
							<select
								value={mode}
								onChange={(e) => setMode(e.target.value)}
								disabled={isLoading}
							>
								<option value="update">Update existing, create new</option>
								<option value="skip">Skip existing, create new only</option>
								<option value="fail">Fail if duplicates found</option>
							</select>
						</label>
						
						<span className="selected-count">
							{selectedMaterials.size} of {extractedMaterials.length} selected
						</span>
					</div>

					{/* Import Actions */}
					<div className="import-actions">
						<button
							onClick={() => handleImport(true)}
							disabled={selectedMaterials.size === 0 || isLoading}
							className="btn-secondary"
						>
							{isLoading ? "Processing..." : "🧪 Dry Run"}
						</button>
						<button
							onClick={() => handleImport(false)}
							disabled={selectedMaterials.size === 0 || isLoading}
							className="btn-primary"
						>
							{isLoading ? "Importing..." : "✅ Import Selected"}
						</button>
					</div>
				</div>
			)}

			{/* Import Results */}
			{importResult && (
				<div className={`import-result ${importResult.success ? "success" : "error"}`}>
					<div className="result-header">
						<h4>
							{importResult.dry_run ? "🧪 Dry Run Results" : "Import Results"}
							{importResult.success ? " ✓" : " ✗"}
						</h4>
						<button onClick={handleReset} className="btn-text">
							Start Over
						</button>
					</div>

					<div className="result-summary">
						<div className="summary-item total">
							<span className="value">{importResult.summary?.total_rows || 0}</span>
							<span className="label">Total</span>
						</div>
						<div className="summary-item created">
							<span className="value">{importResult.summary?.created || 0}</span>
							<span className="label">Created</span>
						</div>
						<div className="summary-item updated">
							<span className="value">{importResult.summary?.updated || 0}</span>
							<span className="label">Updated</span>
						</div>
						<div className="summary-item skipped">
							<span className="value">{importResult.summary?.skipped || 0}</span>
							<span className="label">Skipped</span>
						</div>
						<div className="summary-item errors">
							<span className="value">{importResult.summary?.errors || 0}</span>
							<span className="label">Errors</span>
						</div>
					</div>

					{/* Processed Materials */}
					{importResult.materials_processed && importResult.materials_processed.length > 0 && (
						<div className="processed-materials">
							<h5>Materials Processed:</h5>
							<ul>
								{importResult.materials_processed.slice(0, 15).map((mat, i) => (
									<li key={i} className={`action-${mat.action}`}>
										<span className="action-badge">{mat.action}</span>
										{mat.name} — {formatCurrency(parseFloat(mat.cost_per_sqft))}/sqft
									</li>
								))}
								{importResult.materials_processed.length > 15 && (
									<li className="more">
										...and {importResult.materials_processed.length - 15} more
									</li>
								)}
							</ul>
						</div>
					)}

					{/* Warnings */}
					{importResult.warnings && importResult.warnings.length > 0 && (
						<div className="result-warnings">
							<h5>⚠️ Warnings ({importResult.warnings.length})</h5>
							<ul>
								{importResult.warnings.slice(0, 5).map((warning, i) => (
									<li key={i}>
										{warning.name && <strong>{warning.name}:</strong>} {warning.message}
									</li>
								))}
								{importResult.warnings.length > 5 && (
									<li className="more">
										...and {importResult.warnings.length - 5} more
									</li>
								)}
							</ul>
						</div>
					)}

					{/* Errors */}
					{importResult.errors && importResult.errors.length > 0 && (
						<div className="result-errors">
							<h5>❌ Errors ({importResult.errors.length})</h5>
							<ul>
								{importResult.errors.slice(0, 5).map((err, i) => (
									<li key={i}>
										{err.name && <strong>{err.name}:</strong>} {err.message}
									</li>
								))}
								{importResult.errors.length > 5 && (
									<li className="more">
										...and {importResult.errors.length - 5} more
									</li>
								)}
							</ul>
						</div>
					)}

					{/* Dry Run: Proceed Button */}
					{importResult.dry_run && importResult.success && (
						<div className="import-actions">
							<button
								onClick={() => handleImport(false)}
								disabled={isLoading}
								className="btn-primary"
							>
								{isLoading ? "Importing..." : "✅ Proceed with Import"}
							</button>
						</div>
					)}

					{/* Success Message */}
					{!importResult.dry_run && importResult.success && (
						<div className="success-message">
							✅ Materials imported successfully! 
							{importResult.summary?.created > 0 && ` ${importResult.summary.created} new materials added.`}
							{importResult.summary?.updated > 0 && ` ${importResult.summary.updated} materials updated.`}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

export default MaterialImport;
